import React from 'react';
import ReactDOM from 'react-dom/client';
import axios from 'axios';
import { createStore, applyMiddleware, compose } from 'redux';
import ReduxThunk from 'redux-thunk';
import { Provider } from 'react-redux';
import { routerMiddleware } from 'connected-react-router';
import { ThemeProvider as ThemeProviderV5 } from '@mui/material/styles';
import { StyledEngineProvider } from '@mui/material/styles';
import App from './components/App';
import * as serviceWorker from './serviceWorker';
import { LayoutProvider } from './context/LayoutContext';
import { AuthProvider } from './context/AuthContext';
import { UserProvider } from './context/UserContext';
import { ManagementProvider } from './context/ManagementContext';
import createRootReducer from './reducers';
import {
  ThemeProvider as ThemeChangeProvider,
  ThemeStateContext,
} from './context/ThemeContext';
import CssBaseline from '@mui/material/CssBaseline';
import config from '../src/config';

import { createBrowserHistory, createMemoryHistory } from 'history';

const history =
  typeof window !== 'undefined'
    ? createBrowserHistory()
    : createMemoryHistory({
        initialEntries: [],
      });

export function getHistory() {
  return history;
}

const normalizeBaseUrl = (base) => {
  const s = String(base || '').trim();
  return s ? s.replace(/\/+$/, '') : '';
};

const API_BASE_URL = normalizeBaseUrl(process.env.REACT_APP_API_BASE_URL || config.baseURLApi);
let API_ORIGIN = '';
try {
  API_ORIGIN = API_BASE_URL ? new URL(API_BASE_URL).origin : '';
} catch {
  API_ORIGIN = '';
}

// Align axios base URL with the environment API so relative paths hit the same backend
axios.defaults.baseURL = API_BASE_URL || config.baseURLApi;
axios.defaults.headers.common['Content-Type'] = 'application/json';
const token = localStorage.getItem('token');
if (token) {
  axios.defaults.headers.common['Authorization'] = 'Bearer ' + token;
}

// Ensure axios always picks up the latest token (and works with relative URLs)
axios.interceptors.request.use(
  (req) => {
    const activeToken = localStorage.getItem('token');
    if (!activeToken) return req;

    const headers = req.headers || {};
    const hasAuthHeader = Object.keys(headers).some((k) => String(k).toLowerCase() === 'authorization');
    if (hasAuthHeader) return req;

    const url = String(req.url || '');
    const isAbsolute = /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(url) || url.startsWith('//');
    if (isAbsolute) {
      try {
        const abs = new URL(url.startsWith('//') ? `${window.location.protocol}${url}` : url);
        if (API_ORIGIN && abs.origin !== API_ORIGIN) return req;
      } catch {
        // If URL parsing fails, don't risk attaching token to unknown origins
        return req;
      }
    }

    req.headers = { ...headers, Authorization: `Bearer ${activeToken}` };
    return req;
  },
  (error) => Promise.reject(error),
);

// Ensure all fetch-based API calls include the auth token
if (typeof window !== 'undefined' && window.fetch) {
  const originalFetch = window.fetch.bind(window);
  window.fetch = (input, init = {}) => {
    const activeToken = localStorage.getItem('token');
    if (!activeToken) return originalFetch(input, init);

    const url =
      typeof input === 'string'
        ? input
        : input instanceof URL
          ? input.toString()
          : input instanceof Request
            ? input.url
            : String(input?.url || '');

    if (!url) return originalFetch(input, init);

    const isAbsolute = /^[a-zA-Z][a-zA-Z\d+\-.]*:/.test(url) || url.startsWith('//');

    // Only attach auth for:
    // - relative URLs (works with proxies/rewrites like /api/*)
    // - absolute URLs that target the configured API origin
    if (isAbsolute) {
      try {
        const abs = new URL(url.startsWith('//') ? `${window.location.protocol}${url}` : url);
        if (API_ORIGIN && abs.origin !== API_ORIGIN) {
          return originalFetch(input, init);
        }
      } catch {
        return originalFetch(input, init);
      }
    }

    const merged = new Headers();
    if (input instanceof Request) {
      try {
        new Headers(input.headers).forEach((v, k) => merged.set(k, v));
      } catch {}
    }
    try {
      new Headers(init.headers || {}).forEach((v, k) => merged.set(k, v));
    } catch {}

    if (!merged.has('Authorization')) {
      merged.set('Authorization', `Bearer ${activeToken}`);
    }

    if (input instanceof Request) {
      try {
        const req = new Request(input, { ...init, headers: merged });
        return originalFetch(req);
      } catch {
        return originalFetch(input, { ...init, headers: merged });
      }
    }

    return originalFetch(url, { ...init, headers: merged });
  };
}

export const store = createStore(
  createRootReducer(history),
  compose(applyMiddleware(routerMiddleware(history), ReduxThunk)),
);

const root = ReactDOM.createRoot(document.getElementById('root'));

root.render(
  <Provider store={store}>
    <AuthProvider>
      <LayoutProvider>
        <UserProvider>
          <StyledEngineProvider injectFirst>
            <ThemeChangeProvider>
              <ThemeStateContext.Consumer>
                {(theme) => (
                  <ThemeProviderV5 theme={theme}>
                    <ManagementProvider>
                      <CssBaseline />
                      <App />
                    </ManagementProvider>
                  </ThemeProviderV5>
                )}
              </ThemeStateContext.Consumer>
            </ThemeChangeProvider>
          </StyledEngineProvider>
        </UserProvider>
      </LayoutProvider>
    </AuthProvider>
  </Provider>,
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: http://bit.ly/CRA-PWA
serviceWorker.unregister();
